import {Meta} from '@rdcomponents/meta/meta';
import {Text} from '@rdcomponents/text/text';
import {useMutation} from '@tanstack/react-query';
import {Button, Flex, Form} from 'antd';
import {noop} from 'lodash';
import {useCallback} from 'react';

import {appRoute} from '../../../../../app-route';
import {spacer_m} from '../../../../../css/var-export.scss';
import {BreadCrumbs} from '../../../../../layout/bread-crumbs/bread-crumbs';
import {Page} from '../../../../../layout/page/page';
import {MainPageContainer} from '../../../../../layout/page-card/main-page-card';
import {PageCard} from '../../../../../layout/page-card/page-card';
import {PageHeader} from '../../../../../layout/page-header/page-header';
import {useLocale} from '../../../../../provider/locale/locale-hook';
import {Locale} from '../../../../../provider/locale/localization';
import {useSnackbar} from '../../../../../provider/snackbar/snackbar-hook';
import {useGoBackHook} from '../../../../../service/navigation/go-back-hook';
import {CustomReviewError, handleCustomReviewError} from '../../../../../service/reviews/custom-reviews';
import {
    fetchImportCustomReviews,
    ImportCustomReviewsPayloadType,
    ImportCustomReviewsResponseType,
} from '../../../../../service/reviews/reviews-custom-reviews-import-helper';
import {useUrl} from '../../../../../util/url-hook/url-hook';
import * as reviewFormStyles from '../../review-form/review-form.scss';

import {ImportReviewsBrandCompanyScopeCard} from './import-reviews-brand-company-scope-card';
import {getImportReviewsFormFieldErrors, getImportReviewsSnackbarErrorMessage} from './import-reviews-error-helper';
import {ImportReviewsFileUpload} from './import-reviews-file-upload';
import {serializeImportReviewsForm} from './import-reviews-form-helper';
import {
    ImportReviewsEntityScopeEnum,
    ImportReviewsFormFieldNamesEnum,
    ImportReviewsFormFieldsType,
} from './import-reviews-form-type';
import {ImportReviewsTagsField} from './import-reviews-tags-field';

export function ImportReviews(): JSX.Element {
    const {getLocalizedString} = useLocale();
    const {snackbar} = useSnackbar();
    const {pushUrl} = useUrl();
    const goBack = useGoBackHook(appRoute.reviewsManagement.path);
    const pageTitle = getLocalizedString('CATEGORY_NAME__REVIEWS__IMPORT_REVIEWS');

    const [form] = Form.useForm<ImportReviewsFormFieldsType>();

    const {mutate: importReviews, isLoading: isImportInProgress} = useMutation<
        ImportCustomReviewsResponseType,
        unknown,
        ImportCustomReviewsPayloadType
    >({
        mutationFn: fetchImportCustomReviews,
        onSuccess: ({createdCount}) => {
            snackbar.success(<Locale stringKey="REVIEW_IMPORT__SUCCESS" valueMap={{count: createdCount}} />);

            pushUrl(appRoute.reviewsManagement.path);
        },
        onError: (error: unknown) => {
            const fieldErrors = getImportReviewsFormFieldErrors(error, getLocalizedString);

            if (fieldErrors.length > 0) {
                form.setFields(fieldErrors);
                return;
            }

            if (error instanceof CustomReviewError) {
                snackbar.error(error.message);
                handleCustomReviewError(error);
                return;
            }

            const errorMessage = getImportReviewsSnackbarErrorMessage(error);

            if (errorMessage) {
                snackbar.error(errorMessage);
                return;
            }

            snackbar.error({
                message: <Locale stringKey="ERROR__SOMETHING_WENT_WRONG" />,
                description: <Locale stringKey="ERROR__SOMETHING_WENT_WRONG_DETAILS" />,
            });
        },
    });

    const handleSubmit = useCallback(async () => {
        await form
            .validateFields()
            .then((values) => {
                importReviews(serializeImportReviewsForm(values));
            })
            .catch(noop);
    }, [form, importReviews]);

    const handleCancel = useCallback(() => {
        form.resetFields();
        goBack();
    }, [form, goBack]);

    return (
        <Page isTopFilterHidden renderContainer={(children) => <MainPageContainer>{children}</MainPageContainer>}>
            <Meta title={pageTitle} />
            <PageCard>
                <BreadCrumbs
                    list={[
                        {path: appRoute.reviewsManagement.path, titleLangKey: 'CATEGORY_NAME__REVIEWS'},
                        {
                            path: appRoute.reviewsManagementImportReviews.path,
                            titleLangKey: 'CATEGORY_NAME__REVIEWS__IMPORT_REVIEWS',
                        },
                    ]}
                />

                <PageHeader>
                    {pageTitle}
                    <Button onClick={goBack}>
                        <Text stringKey="BUTTON__BACK" />
                    </Button>
                </PageHeader>
            </PageCard>

            <Form<ImportReviewsFormFieldsType>
                className={reviewFormStyles.ReviewForm_formContainer}
                form={form}
                initialValues={{
                    [ImportReviewsFormFieldNamesEnum.entityScope]: ImportReviewsEntityScopeEnum.brand,
                }}
                layout="vertical"
            >
                <PageCard title={<Locale stringKey="REVIEW__CUSTOM_FORM__GENERAL_SETTINGS" />}>
                    <ImportReviewsBrandCompanyScopeCard form={form} />
                    <ImportReviewsFileUpload />
                    <ImportReviewsTagsField />
                </PageCard>

                <PageCard>
                    <Flex gap={spacer_m}>
                        <Button
                            disabled={isImportInProgress}
                            loading={isImportInProgress}
                            onClick={handleSubmit}
                            type="primary"
                        >
                            <Locale stringKey="BUTTON__SAVE" />
                        </Button>
                        <Button disabled={isImportInProgress} onClick={handleCancel}>
                            <Locale stringKey="BUTTON__CANCEL" />
                        </Button>
                    </Flex>
                </PageCard>
            </Form>
        </Page>
    );
}
